import { EASE } from "@nnf3d/lib";
import { Link, useSearch } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { AnimatePresence, m } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useGlobal } from "../lib/global-context";
import { useStoreEffect } from "../lib/hooks/use-store-effect";
import { focusedPillarIndexStore, hoverPillarSlugStore, routeStore } from "../lib/store";
import type { VoidComponent } from "../lib/types";
import { MobileIslandSwipe } from "./mobile-island-swipe";
import { ScrollIndicator } from "./scroll-indicator";

const titleTransition = { duration: 0.4, ease: EASE.OUT_QUART };
const titleInitial = { opacity: 0, y: "0.5rem" };
const titleAnimate = { opacity: 1, y: 0 };
const titleExit = { opacity: 0, y: "-0.5rem" };

const PillarCarousel: VoidComponent = () => {
	const { pillars } = useGlobal();
	const level = useStore(routeStore, (state) => state.level);
	const search = useSearch({ from: "__root__" });
	const focusedPillarIndex = useStore(focusedPillarIndexStore);
	const [animationKey, setAnimationKey] = useState(0);
	const isAutoAdvanceRef = useRef(false);
	const prevMobileIndexRef = useRef(focusedPillarIndex);

	// Use focusedPillarIndex as the single source of truth for current pillar
	const currentPillar = pillars[focusedPillarIndex];

	const currentPillarParams = useMemo(
		() => ({ pillar: currentPillar?.slug.current ?? "" }),
		[currentPillar?.slug],
	);

	const advanceCarousel = useCallback(() => {
		isAutoAdvanceRef.current = true;

		const nextIndex = (focusedPillarIndex + 1) % pillars.length;
		const nextSlug = pillars[nextIndex]?.slug.current;

		focusedPillarIndexStore.setState(nextIndex);

		if (nextSlug) {
			hoverPillarSlugStore.setState(nextSlug);
		}
	}, [focusedPillarIndex, pillars]);

	// Set initial hover state on mount/route change
	useStoreEffect(routeStore, ({ currentVal }) => {
		if (currentVal.level === "index" && currentPillar?.slug.current) {
			hoverPillarSlugStore.setState(currentPillar.slug.current);
		}
	});

	// Sync animation key when focusedPillarIndex changes (from swipe or auto-advance)
	useEffect(() => {
		if (focusedPillarIndex !== prevMobileIndexRef.current) {
			// Only increment animation key if it was an external change (swipe)
			if (!isAutoAdvanceRef.current) {
				setAnimationKey((prev) => prev + 1);
			}

			isAutoAdvanceRef.current = false;
			prevMobileIndexRef.current = focusedPillarIndex;
		}
	}, [focusedPillarIndex]);

	const handleAnimationEnd = useCallback(() => {
		setAnimationKey((prev) => prev + 1);
		advanceCarousel();
	}, [advanceCarousel]);

	const animation = useMemo<Parameters<typeof m.div>[0]>(
		() => ({
			initial: { opacity: 0, y: "100%" },
			animate: { opacity: 1, y: "0" },
			exit: { opacity: 0, y: "0.5rem", transition: { duration: 0 } },
			transition: { duration: 0.5, ease: EASE.OUT_QUART },
		}),
		[],
	);

	return (
		<AnimatePresence>
			{level === "index" && currentPillar && search?.about !== 1 && (
				<m.div {...animation} className="fixed right-4 bottom-4 left-4 z-100 desktop:hidden">
					<MobileIslandSwipe />

					<ScrollIndicator />

					<Link
						to="/$pillar"
						params={currentPillarParams}
						className="block h-18 bg-surface-primary"
					>
						<div
							key={`line-${animationKey}`}
							className="carousel-progress-line"
							onAnimationEnd={handleAnimationEnd}
						/>

						<AnimatePresence mode="wait">
							<m.div
								key={currentPillar.slug.current}
								className="absolute top-4 right-4 left-4"
								initial={titleInitial}
								animate={titleAnimate}
								exit={titleExit}
								transition={titleTransition}
							>
								<p className="text-sm leading-5 text-foreground">{currentPillar.title}</p>
							</m.div>
						</AnimatePresence>
					</Link>
				</m.div>
			)}
		</AnimatePresence>
	);
};

export { PillarCarousel };
